import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Save, Loader2, RotateCcw } from 'lucide-react'
import { updateOrderFees, resetOrderFees } from '../lib/api'
import { ToastContainer, useToast } from './Toast'

function formatCurrency(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2,
  }).format(v ?? 0)
}

const FEE_FIELDS = [
  { key: 'pick_fee',      label: 'Pick' },
  { key: 'pack_fee',      label: 'Additional Items' },
  { key: 'handling_fee',  label: 'Labelling' },
  { key: 'insert_fee',    label: 'Insert' },
  { key: 'tag_fee',       label: 'Tag fee' },
  { key: 'shipping_cost', label: 'Shipping (charged)' },
  { key: 'carrier_cost',  label: 'Carrier cost' },
]

/**
 * Shared per-order fee editor — used by the Self Ship tab and the Orders table.
 * Saving locks the order (manually_edited_fees=True) so reprice/sync leave it be;
 * Reset clears the lock. orderId resolves from row.order_id (Self Ship) or row.id
 * (orders). Pass invalidateKeys (array of query keys) to refresh the right list.
 */
export default function EditOrderFeesModal({ row, onClose, invalidateKeys = [['fulfillments']] }) {
  const orderId = row.order_id || row.id
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()
  const invalidate = () => invalidateKeys.forEach(k => queryClient.invalidateQueries({ queryKey: k }))

  const [values, setValues] = useState(() => {
    const out = {}
    FEE_FIELDS.forEach(({ key }) => {
      const v = row[key]
      out[key] = v == null ? '' : String(v)
    })
    return out
  })

  const total = FEE_FIELDS.reduce((sum, { key }) => {
    if (key === 'carrier_cost') return sum   // carrier_cost is the 3PL's cost, not a client charge
    const n = parseFloat(values[key])
    return sum + (Number.isFinite(n) ? n : 0)
  }, 0)

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {}
      FEE_FIELDS.forEach(({ key }) => {
        const raw = values[key]
        if (raw === '' || raw == null) return
        const n = parseFloat(raw)
        if (Number.isFinite(n)) payload[key] = n
      })
      return updateOrderFees(orderId, payload)
    },
    onSuccess: () => {
      invalidate()
      addToast({ type: 'success', message: 'Fees updated — order is now locked from auto-pricing' })
      onClose()
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || err.message || 'Update failed',
    }),
  })

  const resetMut = useMutation({
    mutationFn: () => resetOrderFees(orderId),
    onSuccess: () => {
      invalidate()
      addToast({ type: 'success', message: 'Manual lock cleared. Run Reprice Orders to apply current rate card.' })
      onClose()
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || err.message || 'Reset failed',
    }),
  })

  function handleChange(key, raw) {
    setValues(prev => ({ ...prev, [key]: raw }))
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
          <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
            <div>
              <h3 className="font-semibold text-slate-800">Edit Order Fees</h3>
              <p className="text-xs text-slate-400 mt-0.5 font-mono">
                Order {row.order_number || row.shipstation_order_id}
              </p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X size={18} />
            </button>
          </div>

          <form onSubmit={e => { e.preventDefault(); mutation.mutate() }} className="px-6 py-5 space-y-3">
            {FEE_FIELDS.map(({ key, label }) => (
              <div key={key} className="grid grid-cols-[1.4fr_1fr] gap-3 items-center">
                <label className="text-sm text-slate-600">{label}</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={values[key]}
                    onChange={e => handleChange(key, e.target.value)}
                    className="input pl-6 text-sm font-mono text-right"
                    placeholder="0.00"
                  />
                </div>
              </div>
            ))}

            <div className="grid grid-cols-[1.4fr_1fr] gap-3 items-center pt-3 border-t border-slate-200">
              <span className="text-sm font-semibold text-slate-700">
                Client total <span className="text-xs text-slate-400 font-normal">(excl. carrier)</span>
              </span>
              <div className="text-right text-sm font-semibold text-slate-800 tabular-nums pr-3">
                {formatCurrency(total)}
              </div>
            </div>

            <p className="text-xs text-slate-400 pt-1 leading-relaxed">
              Saving locks the fees on this order. Reprice Orders and future syncs will leave the
              fees alone. Use <b>Reset to auto-pricing</b> to unlock — the order will pick up the
              current rate card on the next Reprice.
            </p>

            <div className="flex justify-between items-center gap-2 pt-2">
              {row.manually_edited_fees ? (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Clear the manual lock on this order? Reprice Orders will then overwrite these fees with the current rate card.')) {
                      resetMut.mutate()
                    }
                  }}
                  disabled={resetMut.isPending}
                  className="text-amber-600 hover:text-amber-700 text-xs flex items-center gap-1"
                >
                  {resetMut.isPending ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                  Reset to auto-pricing
                </button>
              ) : <div />}
              <div className="flex gap-2">
                <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={mutation.isPending} className="btn-primary">
                  {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  )
}
